import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Tooltip } from '../../admin/components/shared/Tooltip.tsx';
import { AppSelect } from '../../shared/common/AppSelect.tsx';
import { ListPageSearchInput } from '../../shared/list-page/ListPageSearchInput.tsx';
import { RCM_ALL_CLASSIFICATION_FILTER_OPTIONS } from './rcmAllListConstants.js';
import { RCM_RISK_CATEGORY_FILTER_OPTIONS } from './rcmFilterConstants.js';

/**
 * All-tab RCM filters: search + classification, SOX cycle, domain, system, risk category.
 * Domain/system apply to ITGC rows; SOX cycle/risk category apply to process rows.
 */
export function RcmAllListFilters({
  searchQuery,
  onSearchQueryChange,
  filterClassification,
  onFilterClassificationChange,
  filterSoxCycle,
  onFilterSoxCycleChange,
  soxCycleOptions = [],
  filterDomain,
  onFilterDomainChange,
  domainOptions = [],
  domainFilterEnabled = true,
  filterSystem,
  onFilterSystemChange,
  systemOptions = [],
  systemFilterEnabled = true,
  filterRiskCategory,
  onFilterRiskCategoryChange,
  riskCategoryFilterEnabled = true,
  soxCycleFilterEnabled = true,
  onReset,
  columnVisibilityMenu = null,
}) {
  return (
    <div className="shrink-0 relative bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2 mb-2">
      <div className="flex flex-wrap 2xl:flex-nowrap items-center gap-2.5 min-w-0">
        <div className="flex-[1_1_18rem] min-w-[15rem]">
          <ListPageSearchInput
            placeholder="Search cycle, process, risk, control, system, domain..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            aria-label="Search combined RCM rows"
          />
        </div>
        <div className="flex-[1_1_9.5rem] min-w-[9.5rem] max-w-[11.5rem]">
          <AppSelect
            value={filterClassification}
            onChange={(e) => onFilterClassificationChange(e.target.value)}
            aria-label="Filter by classification"
            options={RCM_ALL_CLASSIFICATION_FILTER_OPTIONS}
            wrapperClassName="w-full"
          />
        </div>
        <div className="flex-[1_1_9.5rem] min-w-[9.5rem] max-w-[11.5rem]">
          <AppSelect
            value={filterSoxCycle}
            onChange={(e) => onFilterSoxCycleChange(e.target.value)}
            aria-label="Filter by SOX cycle"
            disabled={!soxCycleFilterEnabled}
            options={soxCycleOptions}
            wrapperClassName="w-full"
          />
        </div>
        <div className="flex-[1_1_9.5rem] min-w-[9.5rem] max-w-[11.5rem]">
          <AppSelect
            value={filterDomain}
            onChange={(e) => onFilterDomainChange(e.target.value)}
            aria-label="Filter by ITGC domain"
            disabled={!domainFilterEnabled}
            options={domainOptions}
            wrapperClassName="w-full"
          />
        </div>
        <div className="flex-[1_1_9.5rem] min-w-[9.5rem] max-w-[11.5rem]">
          <AppSelect
            value={filterSystem}
            onChange={(e) => onFilterSystemChange(e.target.value)}
            aria-label="Filter by system"
            disabled={!systemFilterEnabled}
            options={systemOptions}
            wrapperClassName="w-full"
          />
        </div>
        <div className="flex-[1_1_9.5rem] min-w-[9.5rem] max-w-[11.5rem]">
          <AppSelect
            value={filterRiskCategory}
            onChange={(e) => onFilterRiskCategoryChange(e.target.value)}
            aria-label="Filter by risk category"
            disabled={!riskCategoryFilterEnabled}
            options={RCM_RISK_CATEGORY_FILTER_OPTIONS}
            wrapperClassName="w-full"
          />
        </div>
        <Tooltip content="Reset filters" side="left">
          <button
            type="button"
            onClick={onReset}
            aria-label="Reset filters"
            className="z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
        {columnVisibilityMenu}
      </div>
    </div>
  );
}
